import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, dateFnsLocalizer, type SlotInfo, Views } from 'react-big-calendar';
import withDragAndDrop, {
  type EventInteractionArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';
import { useKeyedHoverPopover } from '@components/ui/HoverPopover';
import {
  getLabelColor,
  useAppointmentLabelsEnabled,
  useGetLabelsQuery,
} from '@features/appointment-labels';
import {
  type Appointment as AppointmentResponse,
  type AppointmentUpdate,
  clearOptimisticAppointmentPatch,
  setEditingAppointmentId,
  setOptimisticAppointmentPatch,
  useAppointments,
  useBlockedTimesForCalendar,
  useReassignAppointmentMutation,
  useUpdateBlockedTimeRuleMutation,
} from '@features/appointments';
import { useClinicians } from '@features/organizations';
import { useOrganizationId } from '@features/user';
import { ProviderBadge } from '@jotpsych/ui';
import LabelIcon from '@mui/icons-material/Label';
import { Box } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';
import { format, getDay, parse, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { useSnackbar } from 'notistack';

import { useOrgScheduleAccess } from '../../hooks/useOrgScheduleAccess';
import { useOrgScheduleFilters } from '../../hooks/useOrgScheduleFilters';
import { useProviderNameMap } from '../../hooks/useProviderNameMap';
import { useScheduleReadOnly } from '../../hooks/useScheduleReadOnly';
import { useCalendarGroupBy } from '../../store/hooks';
import type { WeekStartDay } from '../../store/slice';
import type { BlockedTimeDraft, BlockedTimeOccurrence } from '../../types/blockedTime';
import {
  coercePositiveDurationMinutes,
  ORG_WIDE_BLOCK_RESOURCE_ID,
  overlapsRange,
  toUpdateAvailabilityRuleRequest,
  UNAVAILABLE_TITLE,
} from '../../utils/blockedTime';
import AddAppointmentModal from '../AddAppointmentModal';

import { BlockedTimeEventComponent } from './components/BlockedTimeEventComponent';
import CalendarContainer from './components/CalendarContainer';
import CalendarLoadingOverlay from './components/CalendarLoadingOverlay';
import {
  CalendarV2AppointmentPopover,
  CalendarV2EventComponent,
} from './components/CalendarV2EventComponent';
import ClusterEventCard from './components/ClusterEventCard';
import {
  CustomHeader,
  CustomMonthHeader,
  CustomTimeGutterHeader,
} from './components/CustomHeaders';
import { EventComponent } from './components/EventComponent';
import {
  bucketClusterize,
  bucketClusterizeByResource,
  type ClusteredCalendarEvent,
  readableLaneCount,
  sideBySideLanesLayout,
} from './clusterDensity';
import { denseOverlapLayout } from './denseOverlapLayout';
import { type CalendarEvent, type CalendarResource, isBlockedTimeCalendarEvent } from './types';
import {
  DEFAULT_ENCOUNTER_DURATION,
  getAppointmentDisplayTitle,
  getBigCalendarView,
  VALID_STEPS,
  VALID_TIMESLOTS,
} from './utils';

import '../../styles/calendar-custom.scss';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

// Setup the localizer for the calendar. We rebuild it whenever the user's
// `weekStartDay` preference changes — react-big-calendar reads `firstOfWeek`
// from the localizer per render, so a fresh instance is the cleanest signal.
const locales = {
  'en-US': enUS,
};

const buildLocalizer = (weekStartDay: WeekStartDay) =>
  dateFnsLocalizer({
    format,
    parse,
    startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: weekStartDay }),
    getDay,
    locales,
  });

// Create a drag and drop enabled calendar with proper typing
const DnDCalendar = withDragAndDrop<CalendarEvent>(Calendar);
const UNASSIGNED_RESOURCE_ID = '__unassigned__';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const LANE_SEP = '::';
const OTHER_LANE_ID = '__other__';
const MIN_READABLE_DAY_EVENT_CARD_WIDTH_PX = 160;

type CalendarSlotInfo = SlotInfo & {
  slots?: Date[];
  bounds?: unknown;
  box?: unknown;
  resourceId?: string | number;
};

const makeLaneProviderId = (laneId: string, providerId: string): string =>
  `${laneId}${LANE_SEP}${providerId}`;
const parseLaneProviderId = (id: string): { laneId: string; providerId: string } | null => {
  const index = id.indexOf(LANE_SEP);
  if (index === -1) return null;
  return { laneId: id.slice(0, index), providerId: id.slice(index + LANE_SEP.length) };
};
// "Other" is always split by provider (the normal provider calendar); label
// lanes follow the user's "Split lanes by provider" toggle.
const laneIsProviderSplit = (laneId: string, splitLanesByProvider: boolean): boolean =>
  laneId === OTHER_LANE_ID || splitLanesByProvider;

const providerIdFromCalendarResource = (
  resourceId: string | number | undefined,
  isLensActive: boolean,
): string | undefined => {
  if (typeof resourceId !== 'string') return undefined;
  const parsed = parseLaneProviderId(resourceId);
  const providerId = parsed ? parsed.providerId : isLensActive ? undefined : resourceId;
  return providerId &&
    providerId !== UNASSIGNED_RESOURCE_ID &&
    providerId !== ORG_WIDE_BLOCK_RESOURCE_ID
    ? providerId
    : undefined;
};

const isDateOnly = (date: Date): boolean =>
  date.getHours() === 0 &&
  date.getMinutes() === 0 &&
  date.getSeconds() === 0 &&
  date.getMilliseconds() === 0;

const isSameCalendarDate = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const isAllDaySlotSelection = (slotInfo: SlotInfo): boolean => {
  const selection = slotInfo as CalendarSlotInfo;
  const durationMs = selection.end.getTime() - selection.start.getTime();
  const hasTimedGridGeometry = selection.bounds != null || selection.box != null;

  return (
    !hasTimedGridGeometry &&
    durationMs >= MS_PER_DAY &&
    durationMs % MS_PER_DAY === 0 &&
    isDateOnly(selection.start) &&
    isDateOnly(selection.end) &&
    Boolean(selection.slots?.length) &&
    selection.slots.every(isDateOnly)
  );
};

const isTimedMultiDaySlotSelection = (slotInfo: SlotInfo, isAllDaySelection: boolean): boolean => {
  const selection = slotInfo as CalendarSlotInfo;
  return (
    !isAllDaySelection &&
    Boolean(selection.slots?.length) &&
    !isSameCalendarDate(selection.start, selection.end)
  );
};

const blockedTimeToAppointmentResource = (
  blockedTime: BlockedTimeOccurrence,
): AppointmentResponse => ({
  appointment_id: blockedTime.ruleId,
  provider_id: blockedTime.providerId,
  start_time: blockedTime.start.toISOString(),
  duration: coercePositiveDurationMinutes(blockedTime.start, blockedTime.end),
  status: 'scheduled',
  organization_id: blockedTime.rule.organization_id,
  encounter_type_id: null,
  created_at: blockedTime.rule.created_at ?? blockedTime.rule.start_time,
  updated_at: blockedTime.rule.updated_at ?? blockedTime.rule.start_time,
  patient: null,
  provider: null,
  series: null,
  encounter: null,
});

const toBlockedTimeDraftFromOccurrence = (
  blockedTime: BlockedTimeOccurrence,
  start: Date,
  end: Date,
  providerId: string | null | undefined,
  currentUserId?: string,
  isOrgScheduleActive = false,
): BlockedTimeDraft => ({
  scope:
    blockedTime.providerId === null
      ? 'organization'
      : !isOrgScheduleActive && blockedTime.providerId === currentUserId
        ? 'self'
        : 'provider',
  providerId: providerId ?? blockedTime.providerId,
  start,
  end,
  timezone: blockedTime.timezone,
  allDay: blockedTime.allDay,
  rrule: blockedTime.recurrence,
  endDate: blockedTime.rule.end_date ?? null,
});

const ProviderResourceHeader = ({
  resource,
}: {
  label: React.ReactNode;
  index: number;
  resource: object;
}) => {
  const calendarResource = resource as CalendarResource;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        minWidth: 0,
        width: '100%',
        px: 1,
      }}
    >
      <ProviderBadge
        name={calendarResource.id === UNASSIGNED_RESOURCE_ID ? null : calendarResource.title}
        ariaLabel={calendarResource.title}
      />
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 13,
          fontWeight: 650,
          color: '#2F344F',
        }}
      >
        {calendarResource.title}
      </Box>
    </Box>
  );
};

// Fixed height of the colored lane band that tops each lens column. The band is
// the same height on every column — tinted + labeled on label lanes, transparent
// on "Other" — so the band row lines up and a lane's adjacent provider
// sub-columns sit under one continuous bar.
const LENS_BAND_HEIGHT = 20;

const LensResourceHeader = ({
  resource,
}: {
  label: React.ReactNode;
  index: number;
  resource: object;
}) => {
  const calendarResource = resource as CalendarResource;
  const isOther =
    calendarResource.id === OTHER_LANE_ID ||
    calendarResource.id.startsWith(`${OTHER_LANE_ID}${LANE_SEP}`);

  // Lane label chip (icon + name) — shown on label-lane headers.
  const laneLabel = (
    <>
      <LabelIcon sx={{ fontSize: 14, color: '#2F344F', flexShrink: 0 }} />
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 12,
          fontWeight: 700,
          color: '#2F344F',
        }}
      >
        {calendarResource.title}
      </Box>
    </>
  );

  // Standard provider header (badge + name) — "Other" columns and the lower half
  // of split label-lane columns. Mirrors ProviderResourceHeader so lens headers
  // line up with the plain provider view.
  const providerHeader = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        minWidth: 0,
        width: '100%',
        px: 1,
      }}
    >
      <ProviderBadge
        name={calendarResource.providerName ?? null}
        ariaLabel={calendarResource.providerName ?? calendarResource.title}
      />
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 13,
          fontWeight: 650,
          color: '#2F344F',
        }}
      >
        {calendarResource.providerName ?? calendarResource.title}
      </Box>
    </Box>
  );

  // COMPACT (split off): no band, so lens headers match the standard provider
  // header height — a collapsed label lane is a standard-height tinted header,
  // "Other" is the plain provider header. No band ⇒ no gap over "Other".
  if (!calendarResource.bandMode) {
    if (calendarResource.kind === 'lane') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            minWidth: 0,
            width: '100%',
            height: '100%',
            px: 1,
            bgcolor: getLabelColor(calendarResource.color),
          }}
        >
          {laneLabel}
        </Box>
      );
    }
    return providerHeader;
  }

  // BAND (split on): a fixed-height color band tops every column — tinted +
  // labeled (on the lane's first column) for label lanes, transparent for
  // "Other" — so a lane's provider sub-columns sit under one continuous bar.
  const band = (
    <Box
      sx={{
        height: LENS_BAND_HEIGHT,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        overflow: 'hidden',
        bgcolor: isOther ? 'transparent' : getLabelColor(calendarResource.color),
      }}
    >
      {!isOther && calendarResource.laneStart && laneLabel}
    </Box>
  );

  // A lane with no provider events stays a single column even when split: the
  // band sits over a blank provider-height bar — a hidden provider header
  // reserves exactly the provider-row height so the column lines up with its
  // split peers.
  if (calendarResource.kind === 'lane') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
        {band}
        <Box sx={{ visibility: 'hidden' }}>{providerHeader}</Box>
      </Box>
    );
  }

  // Split label-lane sub-column or "Other": band over the provider header.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
      {band}
      {providerHeader}
    </Box>
  );
};

type AppointmentUpdateResult = boolean | Promise<boolean | undefined> | undefined;

interface CalendarWidgetProps {
  onAppointmentUpdate: (
    appointmentId: string,
    updates: AppointmentUpdate,
    seriesId?: string,
  ) => AppointmentUpdateResult;
  useCalendarV2?: boolean;
  onAppointmentOpen?: (appointmentId: string) => void;
}

const CalendarWidget: React.FC<CalendarWidgetProps> = ({
  onAppointmentUpdate,
  useCalendarV2 = false,
  onAppointmentOpen,
}) => {
  // Get encounters data from context
  const {
    appointments,
    isLoading,
    isBackgroundLoading,
    viewMode,
    setViewMode,
    currentDate,
    setCurrentDate,
    calendarGranularity,
    weekStartDay,
    calendarVisibleHours,
    clusterBusyHours,
    scrollToNowRequestId,
    refreshAppointments,
  } = useAppointments();

  const { groupByMode, groupByLabelGroupId, splitLanesByProvider } = useCalendarGroupBy();
  const organizationId = useOrganizationId();
  const labelsEnabled = useAppointmentLabelsEnabled();
  const { data: labelsData } = useGetLabelsQuery(
    { orgId: organizationId ?? '' },
    { skip: !organizationId || !labelsEnabled },
  );
  // Gate the lens on the feature flag too: groupByMode is persisted, so a saved
  // lens would otherwise still render the lanes after the flag is turned off.
  const isLensActive =
    labelsEnabled &&
    useCalendarV2 &&
    viewMode === 'day' &&
    groupByMode !== 'off' &&
    !!groupByLabelGroupId;

  // The labels that become lanes for the active lens (a whole group, or one label).
  const laneLabels = useMemo(() => {
    if (!isLensActive) return [];
    const all = labelsData?.items ?? [];
    const selected =
      groupByMode === 'group'
        ? all.filter(label => label.label_group_id === groupByLabelGroupId)
        : all.filter(label => label.label_id === groupByLabelGroupId);
    return [...selected].sort(
      (a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name),
    );
  }, [isLensActive, groupByMode, groupByLabelGroupId, labelsData]);

  const laneLabelIds = useMemo(
    () => new Set(laneLabels.map(label => label.label_id)),
    [laneLabels],
  );

  // The lane an appointment belongs to: the first of its labels (by lane
  // display order) that the lens covers, else "Other". Display-order — not the
  // appointment's stored label order — so lane assignment is deterministic.
  const resolveLaneId = useCallback(
    (appointment: CalendarEvent['resource']): string => {
      if (laneLabels.length === 0) return OTHER_LANE_ID;
      const present = new Set((appointment.labels ?? []).map(label => label.label_id));
      const lane = laneLabels.find(label => present.has(label.label_id));
      return lane ? lane.label_id : OTHER_LANE_ID;
    },
    [laneLabels],
  );

  const localizer = useMemo(() => buildLocalizer(weekStartDay), [weekStartDay]);

  // `min`/`max` from react-big-calendar are dates whose time-of-day defines the
  // visible scroll range. We anchor them to the calendar's current date so the
  // displayed bounds stay aligned with whatever day/week the user is on.
  const [minTime, maxTime] = useMemo(() => {
    const min = new Date(currentDate);
    min.setHours(calendarVisibleHours.startHour, 0, 0, 0);
    const max = new Date(currentDate);
    if (calendarVisibleHours.endHour >= 24) {
      // 24 means "midnight at end of day" — set to 23:59:59 to keep within the day.
      max.setHours(23, 59, 59, 999);
    } else {
      max.setHours(calendarVisibleHours.endHour, 0, 0, 0);
    }
    return [min, max];
  }, [currentDate, calendarVisibleHours.startHour, calendarVisibleHours.endHour]);

  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { blockedTime: blockedTimeTracking, orgSchedule, scheduleDensity } = useEventTracking();
  const [reassignAppointment] = useReassignAppointmentMutation();
  const [updateBlockedTimeRule] = useUpdateBlockedTimeRuleMutation();
  const isReadOnly = useScheduleReadOnly();
  const { currentUserId, hasOrgScheduleAccess } = useOrgScheduleAccess();
  const { isOrgViewActive, selectedProviderIds } = useOrgScheduleFilters();
  const isOrgScheduleActive = hasOrgScheduleAccess && isOrgViewActive;
  const { clinicians } = useClinicians({ skip: !isOrgScheduleActive });

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [dayEventLaneWidth, setDayEventLaneWidth] = useState<number | null>(null);
  const calendarContainerRef = useRef<HTMLDivElement | null>(null);

  // Validate and sanitize calendar granularity to prevent invalid array length
  const safeStep = useMemo(() => {
    const step = calendarGranularity?.step;
    return VALID_STEPS.includes(step) ? step : 15; // Default to 15 if invalid
  }, [calendarGranularity?.step]);

  const safeTimeslots = useMemo(() => {
    const timeslots = calendarGranularity?.timeslots;
    return VALID_TIMESLOTS.includes(timeslots) ? timeslots : 4; // Default to 4 if invalid
  }, [calendarGranularity?.timeslots]);

  // Set initial time to scroll to. We want roughly 2 hours of context before
  // the current time, but never above the user's visible-hours window.
  const scrollToTime = new Date();
  scrollToTime.setHours(
    Math.max(calendarVisibleHours.startHour, scrollToTime.getHours() - 2),
    0,
    0,
    0,
  );

  // State for AddAppointmentModal
  const [isAddAppointmentModalOpen, setIsAddAppointmentModalOpen] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | undefined>(undefined);
  const [selectedDuration, setSelectedDuration] = useState<number | undefined>(undefined);
  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>(undefined);
  const [selectedAllDay, setSelectedAllDay] = useState(false);
  const [selectedBlockedTime, setSelectedBlockedTime] = useState<BlockedTimeOccurrence | null>(
    null,
  );
  // The appointment hover card: one popover shared across every event block,
  // keyed by appointment_id. Timing/intent (rest-open, swap-on-slide,
  // grace-close, pin-on-press) lives in the shared hook.
  const {
    openKey: openPopoverAppointmentId,
    anchorReference: openPopoverAnchorReference,
    onTriggerEnter,
    onTriggerLeave: schedulePopoverClose,
    onSurfacePointerEnter: clearPopoverTimers,
    onSurfacePointerLeave: handlePopoverSurfaceLeave,
    onInteractionPin: handlePopoverInteractionPin,
    close: closeAppointmentPopover,
  } = useKeyedHoverPopover();

  const handlePopoverMouseEnter = useCallback(
    (event: CalendarEvent, anchorEl: HTMLElement) => {
      onTriggerEnter(event.resource.appointment_id, anchorEl);
    },
    [onTriggerEnter],
  );

  // Convert encounters to calendar events when encounters data changes
  useEffect(() => {
    const calendarEvents = appointments.map(appointment => {
      // Use start_time from new model
      const startTime = new Date(appointment.start_time);

      // Calculate end time based on duration
      const endTime = new Date(startTime);

      // Use duration from new model or default
      const durationMinutes = appointment.duration || DEFAULT_ENCOUNTER_DURATION;
      endTime.setMinutes(endTime.getMinutes() + durationMinutes);

      return {
        id: appointment.appointment_id,
        eventType: 'appointment' as const,
        title: getAppointmentDisplayTitle(appointment),
        start: startTime,
        end: endTime,
        resourceId: (() => {
          if (!isLensActive) return appointment.provider_id ?? UNASSIGNED_RESOURCE_ID;
          const laneId = resolveLaneId(appointment);
          return laneIsProviderSplit(laneId, splitLanesByProvider)
            ? makeLaneProviderId(laneId, appointment.provider_id ?? UNASSIGNED_RESOURCE_ID)
            : laneId;
        })(),
        resource: appointment,
      };
    });

    setEvents(calendarEvents);
  }, [appointments, isLensActive, resolveLaneId, splitLanesByProvider]);

  useEffect(() => clearPopoverTimers, [clearPopoverTimers]);

  const providerNameMap = useProviderNameMap();
  const resolveProviderName = useCallback(
    (appointment: CalendarEvent['resource']): string | null =>
      appointment.provider?.name ??
      (appointment.provider_id ? (providerNameMap.get(appointment.provider_id) ?? null) : null),
    [providerNameMap],
  );

  const openPopoverEvent = useMemo(
    () =>
      openPopoverAppointmentId
        ? (events.find(event => event.resource.appointment_id === openPopoverAppointmentId) ?? null)
        : null,
    [events, openPopoverAppointmentId],
  );

  const resources = useMemo<CalendarResource[]>(() => {
    const byId = new Map<string, CalendarResource>();

    const visibleProviderIds =
      selectedProviderIds === null
        ? clinicians.map(clinician => clinician.userId)
        : selectedProviderIds;

    for (const providerId of visibleProviderIds ?? []) {
      const clinician = clinicians.find(candidate => candidate.userId === providerId);
      byId.set(providerId, {
        id: providerId,
        title: clinician?.name ?? 'Assigned provider',
      });
    }

    for (const appointment of appointments) {
      const id = appointment.provider_id ?? UNASSIGNED_RESOURCE_ID;
      if (selectedProviderIds !== null && !selectedProviderIds.includes(id)) continue;
      if (byId.has(id)) continue;
      byId.set(id, {
        id,
        title:
          appointment.provider?.name ??
          (appointment.provider_id ? 'Assigned provider' : 'Unassigned'),
      });
    }

    return Array.from(byId.values()).sort((a, b) => {
      if (a.id === UNASSIGNED_RESOURCE_ID) return 1;
      if (b.id === UNASSIGNED_RESOURCE_ID) return -1;
      return a.title.localeCompare(b.title);
    });
  }, [appointments, clinicians, selectedProviderIds]);

  const lensResources = useMemo<CalendarResource[]>(() => {
    if (!isLensActive) return [];

    const providerTitle = (providerId: string): string =>
      providerId === UNASSIGNED_RESOURCE_ID
        ? 'Unassigned'
        : (providerNameMap.get(providerId) ??
          clinicians.find(clinician => clinician.userId === providerId)?.name ??
          'Assigned provider');

    // Per-lane provider sets (providers that actually have an event in THAT
    // lane) + the providers of "Other" events. Derived from the rendered
    // `events` so columns always match the events' resourceIds — no silent
    // vanish across the events-effect render lag or optimistic drag.
    const providersByLane = new Map<string, Set<string>>();
    for (const calendarEvent of events) {
      const laneId = resolveLaneId(calendarEvent.resource);
      if (laneId === OTHER_LANE_ID) continue;
      const providerId = calendarEvent.resource.provider_id ?? UNASSIGNED_RESOURCE_ID;
      const providers = providersByLane.get(laneId) ?? new Set<string>();
      providers.add(providerId);
      providersByLane.set(laneId, providers);
    }
    const sortByTitle = (ids: Set<string>): string[] =>
      Array.from(ids).sort((a, b) => providerTitle(a).localeCompare(providerTitle(b)));

    const res: CalendarResource[] = [];
    // Label lanes: when split is on, show a column ONLY for providers that have
    // an event in that lane. A lane with no events (or split off) stays a single
    // swimlane.
    for (const label of laneLabels) {
      const laneProviders = providersByLane.get(label.label_id);
      if (splitLanesByProvider && laneProviders && laneProviders.size > 0) {
        sortByTitle(laneProviders).forEach((providerId, index) => {
          res.push({
            id: makeLaneProviderId(label.label_id, providerId),
            title: label.name,
            color: label.color,
            kind: 'lane-provider',
            providerName: providerTitle(providerId),
            // First provider column of the lane carries the spanning band label.
            laneStart: index === 0,
            bandMode: splitLanesByProvider,
          });
        });
      } else {
        res.push({
          id: label.label_id,
          title: label.name,
          color: label.color,
          kind: 'lane',
          laneStart: true,
          bandMode: splitLanesByProvider,
        });
      }
    }
    // "Other" is the normal provider calendar for everything outside the lens —
    // always one column per unfiltered provider (the base roster), even those
    // with nothing scheduled outside the lens. Narrow it with the provider filter.
    for (const providerId of resources.map(resource => resource.id)) {
      res.push({
        id: makeLaneProviderId(OTHER_LANE_ID, providerId),
        title: 'Other',
        kind: 'lane-provider',
        providerName: providerTitle(providerId),
        bandMode: splitLanesByProvider,
      });
    }
    return res;
  }, [
    isLensActive,
    events,
    laneLabels,
    splitLanesByProvider,
    resources,
    providerNameMap,
    clinicians,
    resolveLaneId,
  ]);

  const useProviderResources =
    useCalendarV2 && viewMode === 'day' && groupByMode === 'off' && resources.length > 1;
  const useLensResources = isLensActive && lensResources.length > 0;
  const useResourceColumns = useProviderResources || useLensResources;
  const activeResources = useLensResources ? lensResources : resources;

  useEffect(() => {
    if (!useCalendarV2 || viewMode !== 'day') {
      setDayEventLaneWidth(null);
      return;
    }

    const container = calendarContainerRef.current;
    if (!container) return;

    let animationFrameId: number | null = null;
    const measureLaneWidth = () => {
      animationFrameId = null;
      const eventContainers = Array.from(
        container.querySelectorAll<HTMLElement>('.rbc-time-content .rbc-events-container'),
      );
      const eventContainerWidths = eventContainers
        .map(element => element.getBoundingClientRect().width)
        .filter(width => width > 0);
      const nextLaneWidth =
        eventContainerWidths.length > 0 ? Math.min(...eventContainerWidths) : null;

      setDayEventLaneWidth(previousWidth =>
        nextLaneWidth === null || Math.abs((previousWidth ?? 0) - nextLaneWidth) >= 1
          ? nextLaneWidth
          : previousWidth,
      );
    };

    const scheduleMeasure = () => {
      if (animationFrameId !== null) return;
      animationFrameId = window.requestAnimationFrame(measureLaneWidth);
    };

    scheduleMeasure();
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        if (animationFrameId !== null) {
          window.cancelAnimationFrame(animationFrameId);
        }
      };
    }

    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(container);

    return () => {
      observer.disconnect();
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [activeResources.length, useCalendarV2, useResourceColumns, viewMode]);

  const blockedTimeResourceIds = useMemo(
    () =>
      useCalendarV2 && viewMode === 'day'
        ? resources
            .map(resource => resource.id)
            .filter(id => id !== UNASSIGNED_RESOURCE_ID && id !== ORG_WIDE_BLOCK_RESOURCE_ID)
        : [],
    [resources, useCalendarV2, viewMode],
  );
  const {
    blockedTimes,
    isLoading: isBlockedTimesLoading,
    isFetching: isBlockedTimesFetching,
    refreshBlockedTimes,
  } = useBlockedTimesForCalendar(blockedTimeResourceIds);

  const getBlockedTimeCalendarResourceId = useCallback(
    (blockedTime: BlockedTimeOccurrence): string => {
      if (!isLensActive) {
        return blockedTime.resourceId;
      }
      const providerResourceId =
        blockedTime.resourceId !== ORG_WIDE_BLOCK_RESOURCE_ID
          ? blockedTime.resourceId
          : (blockedTime.providerId ?? UNASSIGNED_RESOURCE_ID);
      return makeLaneProviderId(OTHER_LANE_ID, providerResourceId);
    },
    [isLensActive],
  );

  const blockedTimeEvents = useMemo<CalendarEvent[]>(
    () =>
      blockedTimes.map(blockedTime => ({
        id: blockedTime.id,
        eventType: 'blocked_time' as const,
        title: UNAVAILABLE_TITLE,
        start: blockedTime.start,
        end: blockedTime.end,
        allDay: blockedTime.allDay,
        resourceId: getBlockedTimeCalendarResourceId(blockedTime),
        resource: blockedTimeToAppointmentResource(blockedTime),
        blockedTime,
      })),
    [blockedTimes, getBlockedTimeCalendarResourceId],
  );

  // Capacity clustering (week view): hours with 3+ overlapping appointments
  // collapse into synthetic cluster events rendered by ClusterEventCard.
  // Quiet hours pass through untouched, so low-density schedules look
  // identical with the preference on or off. Blocked-time events are not
  // clustered so they keep their unavailable visual treatment.
  const shouldClusterBusyHours = useCalendarV2 && clusterBusyHours && viewMode === 'week';
  const dayClusterPassThroughMax = readableLaneCount(
    dayEventLaneWidth,
    MIN_READABLE_DAY_EVENT_CARD_WIDTH_PX,
  );
  const displayEvents = useMemo<CalendarEvent[]>(() => {
    const appointmentDisplayEvents = shouldClusterBusyHours
      ? bucketClusterize(events)
      : useCalendarV2 && viewMode === 'day' && Number.isFinite(dayClusterPassThroughMax)
        ? bucketClusterizeByResource(events, { passThroughMax: dayClusterPassThroughMax })
        : events;
    return [...appointmentDisplayEvents, ...blockedTimeEvents];
  }, [
    blockedTimeEvents,
    dayClusterPassThroughMax,
    events,
    shouldClusterBusyHours,
    useCalendarV2,
    viewMode,
  ]);

  // Handle calendar date navigation - this is now handled by CalendarNavigation component
  const handleNavigate = useCallback(
    (newDate: Date) => {
      setCurrentDate(newDate.toISOString());
    },
    [setCurrentDate],
  );

  const warnIfOverlapsBlockedTime = useCallback(
    (start: Date, end: Date, providerId?: string | null) => {
      const overlapsBlockedTime = blockedTimes.some(blockedTime => {
        const appliesToProvider =
          blockedTime.providerId === null ||
          (providerId != null && blockedTime.providerId === providerId);
        return appliesToProvider && overlapsRange(start, end, blockedTime.start, blockedTime.end);
      });

      if (overlapsBlockedTime) {
        enqueueSnackbar(
          'This time overlaps out-of-office time. Existing out-of-office time will stay in place.',
          { variant: 'warning' },
        );
      }
    },
    [blockedTimes, enqueueSnackbar],
  );

  const updateBlockedTimeFromInteraction = useCallback(
    async ({
      blockedTime,
      start,
      end,
      resourceId,
      interaction,
    }: {
      blockedTime: BlockedTimeOccurrence;
      start: Date;
      end: Date;
      resourceId?: string | number;
      interaction: 'drag' | 'resize';
    }) => {
      if (blockedTime.isRecurring) {
        setSelectedBlockedTime(blockedTime);
        setIsAddAppointmentModalOpen(true);
        return;
      }

      const targetProviderResourceId = providerIdFromCalendarResource(resourceId, isLensActive);
      const nextProviderId =
        blockedTime.providerId === null
          ? null
          : (targetProviderResourceId ?? blockedTime.providerId);
      const draft = toBlockedTimeDraftFromOccurrence(
        blockedTime,
        start,
        end,
        nextProviderId,
        currentUserId,
        isOrgScheduleActive,
      );
      const durationMinutes = coercePositiveDurationMinutes(start, end);

      try {
        await updateBlockedTimeRule({
          availabilityRuleId: blockedTime.ruleId,
          availabilityRule: toUpdateAvailabilityRuleRequest(draft),
        }).unwrap();

        if (interaction === 'drag') {
          blockedTimeTracking.dragSubmitted({
            blocked_time_id: blockedTime.ruleId,
            duration_minutes: durationMinutes,
            is_org_schedule: isOrgScheduleActive,
          });
        } else {
          blockedTimeTracking.resizeSubmitted({
            blocked_time_id: blockedTime.ruleId,
            duration_minutes: durationMinutes,
            is_org_schedule: isOrgScheduleActive,
          });
        }
      } catch (error) {
        console.error('Failed to update blocked time:', error);
        enqueueSnackbar('Failed to update out-of-office time. Please try again.', {
          variant: 'error',
        });
        await refreshBlockedTimes();
      }
    },
    [
      blockedTimeTracking,
      currentUserId,
      enqueueSnackbar,
      isLensActive,
      isOrgScheduleActive,
      refreshBlockedTimes,
      updateBlockedTimeRule,
    ],
  );

  // Handle event movement (reschedule, and — in the provider-column day
  // view — reassignment to the provider whose column the event was dropped
  // on; react-big-calendar reports that column as `resourceId`).
  const moveEvent = useCallback(
    async ({ event, start, end, resourceId }: EventInteractionArgs<CalendarEvent>) => {
      if (isBlockedTimeCalendarEvent(event)) {
        await updateBlockedTimeFromInteraction({
          blockedTime: event.blockedTime,
          start: new Date(start),
          end: new Date(end),
          resourceId,
          interaction: 'drag',
        });
        return;
      }

      try {
        const newStartTime = new Date(start);

        const targetResourceId = typeof resourceId === 'string' ? resourceId : undefined;

        if (isLensActive) {
          // Parse the dropped-on column → lane + provider (composite when split
          // by provider, lane-only when collapsed).
          let targetLaneId: string | null = null;
          let targetProviderId: string | undefined;
          if (targetResourceId) {
            const parsed = parseLaneProviderId(targetResourceId);
            if (parsed) {
              targetLaneId = parsed.laneId;
              targetProviderId = parsed.providerId;
            } else {
              targetLaneId = targetResourceId;
            }
          }

          const currentLaneId = resolveLaneId(event.resource);
          const currentLaneLabelId = currentLaneId === OTHER_LANE_ID ? null : currentLaneId;

          // Label change: set the lane's label (replacing any current lens
          // label), or remove it when dropped into "Other".
          const labelsOutsideLens = (event.resource.labels ?? []).filter(
            label => !laneLabelIds.has(label.label_id),
          );
          let nextLabelIds: string[] | undefined;
          let nextLabels = event.resource.labels ?? [];
          if (targetLaneId !== null) {
            if (targetLaneId === OTHER_LANE_ID && currentLaneLabelId !== null) {
              nextLabelIds = labelsOutsideLens.map(label => label.label_id);
              nextLabels = labelsOutsideLens;
            } else if (targetLaneId !== OTHER_LANE_ID && targetLaneId !== currentLaneLabelId) {
              const targetLabel = laneLabels.find(label => label.label_id === targetLaneId);
              nextLabelIds = [...labelsOutsideLens.map(label => label.label_id), targetLaneId];
              nextLabels = targetLabel
                ? [
                    ...labelsOutsideLens,
                    {
                      label_id: targetLabel.label_id,
                      label_group_id: targetLabel.label_group_id,
                      name: targetLabel.name,
                      color: targetLabel.color,
                    },
                  ]
                : labelsOutsideLens;
            }
          }

          // Provider change (composite columns carry a provider).
          const previousProviderId = event.resource.provider_id ?? null;
          const nextProviderId =
            targetProviderId === undefined
              ? undefined
              : targetProviderId === UNASSIGNED_RESOURCE_ID
                ? null
                : targetProviderId;
          const isProviderChange =
            nextProviderId !== undefined && nextProviderId !== previousProviderId;

          // Optimistic local move — patch resourceId + labels + provider so the
          // recompute keeps the event in its new cell until the server confirms.
          const effectiveLaneId =
            nextLabelIds !== undefined
              ? (targetLaneId ?? OTHER_LANE_ID)
              : (currentLaneLabelId ?? OTHER_LANE_ID);
          const effectiveProviderId =
            (isProviderChange ? nextProviderId : previousProviderId) ?? UNASSIGNED_RESOURCE_ID;
          const nextResourceId = laneIsProviderSplit(effectiveLaneId, splitLanesByProvider)
            ? makeLaneProviderId(effectiveLaneId, effectiveProviderId)
            : effectiveLaneId;

          setEvents(prev =>
            prev.map(existingEvent =>
              existingEvent.id === event.id
                ? {
                    ...existingEvent,
                    start: new Date(start),
                    end: new Date(end),
                    resourceId: nextResourceId,
                    resource: {
                      ...existingEvent.resource,
                      labels: nextLabels,
                      ...(isProviderChange && { provider_id: nextProviderId, provider: null }),
                    },
                  }
                : existingEvent,
            ),
          );

          const timeChanged = newStartTime.toISOString() !== event.resource.start_time;
          const sendLensUpdate = () => {
            const lensUpdates: AppointmentUpdate = {
              start_time: newStartTime.toISOString(),
              duration: event.resource.duration,
            };
            if (nextLabelIds !== undefined) lensUpdates.label_ids = nextLabelIds;
            return onAppointmentUpdate(event.id, lensUpdates, event.resource.series?.series_id);
          };

          if (isProviderChange) {
            // Reassign the provider FIRST; only commit the label/time change if
            // it succeeds. The two calls are non-atomic, so a failed reassign
            // would otherwise leave a half-applied drag (label moved, provider
            // didn't).
            dispatch(
              setOptimisticAppointmentPatch({
                appointmentId: event.resource.appointment_id,
                patch: { provider_id: nextProviderId, provider: null },
              }),
            );
            try {
              await reassignAppointment({
                appointmentId: event.resource.appointment_id,
                appointmentReassign: { provider_id: nextProviderId },
              }).unwrap();
              orgSchedule.providerReassigned({
                appointment_id: event.resource.appointment_id,
                previous_provider_id: previousProviderId,
                new_provider_id: nextProviderId,
              });
            } catch (reassignError) {
              dispatch(clearOptimisticAppointmentPatch(event.resource.appointment_id));
              console.error('Failed to reassign appointment provider via drag:', reassignError);
              enqueueSnackbar(
                'Failed to move the appointment to that provider. Please try again.',
                { variant: 'error' },
              );
              await refreshAppointments();
              return;
            }
            // Reassign committed — now apply the label/time change. This leg is
            // also non-atomic: if it fails, warn and resync so the optimistic card
            // doesn't keep showing an unsaved lane/time move.
            const updateResult = await sendLensUpdate();
            if (updateResult === false) {
              console.error('Label/time change failed after drag reassign');
              enqueueSnackbar(
                'Provider moved, but the new lane/time could not be saved. Please retry.',
                { variant: 'warning' },
              );
              await refreshAppointments();
            }
          } else if (timeChanged || nextLabelIds !== undefined) {
            const updateResult = await sendLensUpdate();
            if (updateResult === false) {
              console.error('Label/time change failed on drag');
              enqueueSnackbar('That change could not be saved. Please retry.', {
                variant: 'warning',
              });
              await refreshAppointments();
            }
          }
          return;
        }

        const nextProviderId =
          targetResourceId === undefined
            ? undefined // no resource columns in play (week/month view)
            : targetResourceId === UNASSIGNED_RESOURCE_ID
              ? null
              : targetResourceId;
        const previousProviderId = event.resource.provider_id ?? null;
        const isProviderChange =
          nextProviderId !== undefined && nextProviderId !== previousProviderId;

        warnIfOverlapsBlockedTime(
          new Date(start),
          new Date(end),
          nextProviderId === undefined ? previousProviderId : nextProviderId,
        );

        // Update local state first for immediate feedback (optimistic update)
        const updatedEvents = events.map(existingEvent => {
          if (existingEvent.id === event.id) {
            return {
              ...existingEvent,
              start: new Date(start),
              end: new Date(end),
              ...(isProviderChange && {
                resourceId: nextProviderId ?? UNASSIGNED_RESOURCE_ID,
              }),
            };
          }
          return existingEvent;
        });

        setEvents(updatedEvents);

        // Prepare update data
        const updates: AppointmentUpdate = {
          start_time: newStartTime.toISOString(),
          duration: event.resource.duration,
        };

        // Always use onAppointmentUpdate with optional seriesId
        onAppointmentUpdate(event.id, updates, event.resource.series?.series_id);

        if (isProviderChange) {
          // Same optimistic-patch + reassign + rollback flow as the provider
          // row editor in AppointmentPropertiesTable.
          dispatch(
            setOptimisticAppointmentPatch({
              appointmentId: event.resource.appointment_id,
              patch: { provider_id: nextProviderId, provider: null },
            }),
          );

          try {
            await reassignAppointment({
              appointmentId: event.resource.appointment_id,
              appointmentReassign: { provider_id: nextProviderId },
            }).unwrap();
            orgSchedule.providerReassigned({
              appointment_id: event.resource.appointment_id,
              previous_provider_id: previousProviderId,
              new_provider_id: nextProviderId,
            });
          } catch (reassignError) {
            dispatch(clearOptimisticAppointmentPatch(event.resource.appointment_id));
            console.error('Failed to reassign appointment provider via drag:', reassignError);
            enqueueSnackbar('Failed to move the appointment to that provider. Please try again.', {
              variant: 'error',
            });
            await refreshAppointments();
          }
        }
      } catch (error) {
        console.error('Error updating encounter schedule:', error);
        // If there's an error, do a full refresh to ensure the UI is in sync with server
        await refreshAppointments();
      }
    },
    [
      dispatch,
      enqueueSnackbar,
      events,
      orgSchedule,
      reassignAppointment,
      refreshAppointments,
      onAppointmentUpdate,
      updateBlockedTimeFromInteraction,
      warnIfOverlapsBlockedTime,
      isLensActive,
      laneLabelIds,
      laneLabels,
      splitLanesByProvider,
      resolveLaneId,
    ],
  );

  // Handle event resize
  const resizeEvent = useCallback(
    async ({ event, start, end }: EventInteractionArgs<CalendarEvent>) => {
      if (isBlockedTimeCalendarEvent(event)) {
        await updateBlockedTimeFromInteraction({
          blockedTime: event.blockedTime,
          start: new Date(start),
          end: new Date(end),
          interaction: 'resize',
        });
        return;
      }

      try {
        // Calculate new duration in minutes
        const newStartTime = new Date(start);
        const newEndTime = new Date(end);
        const durationMinutes = Math.round(
          (newEndTime.getTime() - newStartTime.getTime()) / (1000 * 60),
        );

        warnIfOverlapsBlockedTime(newStartTime, newEndTime, event.resource.provider_id ?? null);

        // Update local state first for immediate feedback (optimistic update)
        const updatedEvents = events.map(existingEvent => {
          if (existingEvent.id === event.id) {
            return {
              ...existingEvent,
              start: new Date(start),
              end: new Date(end),
            };
          }
          return existingEvent;
        });

        setEvents(updatedEvents);

        // Prepare update data
        const updates: AppointmentUpdate = {
          start_time: newStartTime.toISOString(),
          duration: durationMinutes,
        };

        // Always use onAppointmentUpdate with optional seriesId
        onAppointmentUpdate(event.id, updates, event.resource.series?.series_id);
      } catch (error) {
        console.error('Error updating encounter duration:', error);
        // If there's an error, do a full refresh to ensure the UI is in sync with server
        await refreshAppointments();
      }
    },
    [
      events,
      refreshAppointments,
      onAppointmentUpdate,
      updateBlockedTimeFromInteraction,
      warnIfOverlapsBlockedTime,
    ],
  );

  const handleClickEvent = useCallback(
    (event: CalendarEvent) => {
      // Cluster cards manage their own member-list popover; selecting the
      // synthetic event itself has no single appointment to open.
      if ((event as ClusteredCalendarEvent).kind === 'cluster') return;

      if (isBlockedTimeCalendarEvent(event)) {
        setSelectedBlockedTime(event.blockedTime);
        setSelectedDateTime(event.start);
        setSelectedDuration(coercePositiveDurationMinutes(event.start, event.end));
        setSelectedProviderId(event.blockedTime.providerId ?? undefined);
        setSelectedAllDay(event.blockedTime.allDay);
        setIsAddAppointmentModalOpen(true);
        return;
      }

      if (useCalendarV2 && onAppointmentOpen) {
        onAppointmentOpen(event.resource.appointment_id);
        return;
      }

      dispatch(setEditingAppointmentId(event.resource.appointment_id));
    },
    [dispatch, onAppointmentOpen, useCalendarV2],
  );

  const handleClusterOpened = useCallback(
    (memberCount: number, providerCount: number) => {
      scheduleDensity.clusterCardClicked({
        member_count: memberCount,
        provider_count: providerCount,
        view_mode: viewMode,
      });
    },
    [scheduleDensity, viewMode],
  );

  const handleClusterAppointmentSelect = useCallback(
    (appointmentId: string, memberCount: number) => {
      scheduleDensity.clusterAppointmentSelected({
        appointment_id: appointmentId,
        member_count: memberCount,
      });
      closeAppointmentPopover();
      if (onAppointmentOpen) {
        onAppointmentOpen(appointmentId);
      } else {
        dispatch(setEditingAppointmentId(appointmentId));
      }
    },
    [closeAppointmentPopover, dispatch, onAppointmentOpen, scheduleDensity],
  );

  // Wrapper for EventComponent with handlers
  const EventComponentWrapper = useCallback(
    ({ event, title }: { event: CalendarEvent; title: React.ReactNode }) => {
      const clusteredEvent = event as ClusteredCalendarEvent;
      if (clusteredEvent.kind === 'cluster') {
        return (
          <ClusterEventCard
            event={clusteredEvent}
            providerNameOf={resolveProviderName}
            onClusterOpened={handleClusterOpened}
            onAppointmentSelect={handleClusterAppointmentSelect}
          />
        );
      }

      if (isBlockedTimeCalendarEvent(event)) {
        return <BlockedTimeEventComponent event={event} viewMode={viewMode} />;
      }

      if (useCalendarV2 && onAppointmentOpen) {
        return (
          <CalendarV2EventComponent
            event={event}
            viewMode={viewMode}
            onOpenAppointmentDetail={appointmentId => {
              closeAppointmentPopover();
              onAppointmentOpen(appointmentId);
            }}
            isPopoverOpen={openPopoverAppointmentId === event.resource.appointment_id}
            onPopoverMouseEnter={handlePopoverMouseEnter}
            onPopoverMouseLeave={schedulePopoverClose}
          />
        );
      }

      return <EventComponent event={event} title={title} viewMode={viewMode} />;
    },
    [
      handlePopoverMouseEnter,
      handleClusterOpened,
      handleClusterAppointmentSelect,
      closeAppointmentPopover,
      onAppointmentOpen,
      openPopoverAppointmentId,
      resolveProviderName,
      schedulePopoverClose,
      useCalendarV2,
      viewMode,
    ],
  );

  // Handle selection slot (click and drag will capture start and end times)
  const handleSelectSlot = useCallback(
    (slotInfo: SlotInfo) => {
      const allDaySelection = isAllDaySlotSelection(slotInfo);
      // Timed-grid multi-day drags are not valid appointment starts; all-day
      // header selections still open out-of-office creation.
      if (isTimedMultiDaySlotSelection(slotInfo, allDaySelection)) {
        return;
      }

      const resourceId = (slotInfo as CalendarSlotInfo).resourceId;
      const providerId = providerIdFromCalendarResource(resourceId, isLensActive);

      warnIfOverlapsBlockedTime(slotInfo.start, slotInfo.end, providerId);

      // Set the start time
      setSelectedDateTime(slotInfo.start);
      setSelectedBlockedTime(null);
      setSelectedProviderId(providerId);
      setSelectedAllDay(allDaySelection);

      // Calculate duration in minutes if there's a difference between start and end
      if (slotInfo.start && slotInfo.end) {
        const durationMinutes = Math.round(
          (slotInfo.end.getTime() - slotInfo.start.getTime()) / (1000 * 60),
        );

        // Only set duration if it's actually a drag selection (more than a few minutes)
        if (durationMinutes > 5) {
          setSelectedDuration(durationMinutes);
        } else {
          setSelectedDuration(undefined);
        }
      }

      setIsAddAppointmentModalOpen(true);
    },
    [isLensActive, warnIfOverlapsBlockedTime],
  );

  // Handle close of AddAppointmentModal
  const handleCloseAddAppointmentModal = useCallback(() => {
    setIsAddAppointmentModalOpen(false);
    setSelectedDateTime(undefined);
    setSelectedDuration(undefined);
    setSelectedProviderId(undefined);
    setSelectedAllDay(false);
    setSelectedBlockedTime(null);
  }, []);

  // Add handler for encounter creation
  const handleAppointmentCreated = useCallback(() => {
    // Refresh encounters to show the new one
    refreshAppointments();
  }, [refreshAppointments]);

  const handleBlockedTimeChanged = useCallback(() => {
    void refreshBlockedTimes();
  }, [refreshBlockedTimes]);

  useEffect(() => {
    if (scrollToNowRequestId === 0 || viewMode === 'month') return;

    let animationFrameId: number | null = null;

    const scrollToNow = () => {
      const scrollContainer =
        calendarContainerRef.current?.querySelector<HTMLElement>('.rbc-time-content');
      if (!scrollContainer) return;

      const now = new Date();
      const contextMinutes = 120;
      const targetMinutes = Math.max(0, now.getHours() * 60 + now.getMinutes() - contextMinutes);
      const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
      const targetScrollTop = Math.min(
        maxScrollTop,
        (targetMinutes / (24 * 60)) * scrollContainer.scrollHeight,
      );

      scrollContainer.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });
    };

    animationFrameId = window.requestAnimationFrame(scrollToNow);

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [scrollToNowRequestId, safeStep, safeTimeslots, viewMode]);

  // eventPropGetter removed - EventComponent now owns all styling

  return (
    <CalendarContainer ref={calendarContainerRef}>
      <CalendarLoadingOverlay
        isLoading={isLoading || isBlockedTimesLoading}
        isBackgroundLoading={isBackgroundLoading || isBlockedTimesFetching}
      />

      <DnDCalendar
        localizer={localizer}
        events={displayEvents}
        resources={useResourceColumns ? activeResources : undefined}
        resourceAccessor="resourceId"
        resourceIdAccessor={(resource: object) => (resource as CalendarResource).id}
        resourceTitleAccessor={(resource: object) => (resource as CalendarResource).title}
        defaultView={getBigCalendarView(viewMode)}
        view={getBigCalendarView(viewMode)}
        views={[Views.DAY, Views.WEEK, Views.MONTH]}
        step={safeStep}
        showMultiDayTimes
        date={currentDate}
        onNavigate={handleNavigate}
        onView={setViewMode}
        scrollToTime={scrollToTime}
        min={minTime}
        max={maxTime}
        onSelectEvent={isReadOnly ? undefined : handleClickEvent}
        onEventDrop={isReadOnly ? undefined : moveEvent}
        onEventResize={isReadOnly ? undefined : resizeEvent}
        selectable={!isReadOnly}
        onSelectSlot={isReadOnly ? undefined : handleSelectSlot}
        resizable={!isReadOnly}
        resizableAccessor={
          isReadOnly
            ? () => false
            : event =>
                viewMode !== 'month' &&
                (event as ClusteredCalendarEvent).kind !== 'cluster' &&
                (!isBlockedTimeCalendarEvent(event) || !event.blockedTime.isRecurring)
        }
        draggableAccessor={
          isReadOnly
            ? () => false
            : event =>
                (event as ClusteredCalendarEvent).kind !== 'cluster' &&
                (!isBlockedTimeCalendarEvent(event) || !event.blockedTime.isRecurring)
        }
        tooltipAccessor={null} // Disable native browser tooltip (ExpandedCard shows details)
        style={{ height: '100%' }}
        timeslots={safeTimeslots}
        dayLayoutAlgorithm={
          shouldClusterBusyHours
            ? sideBySideLanesLayout
            : useCalendarV2 && viewMode === 'day'
              ? sideBySideLanesLayout
              : useCalendarV2 && viewMode !== 'month'
                ? denseOverlapLayout
                : 'no-overlap'
        }
        toolbar={false} // Hide the default toolbar since we have our own navigation
        components={{
          event: EventComponentWrapper,
          ...(useResourceColumns && {
            resourceHeader: useLensResources ? LensResourceHeader : ProviderResourceHeader,
          }),
          week: {
            header: CustomHeader,
          },
          month: {
            header: CustomMonthHeader,
          },
          ...(viewMode === 'day' && {
            timeGutterHeader: () => <CustomTimeGutterHeader currentDate={currentDate} />,
          }),
        }}
      />

      {useCalendarV2 && onAppointmentOpen && openPopoverEvent && openPopoverAnchorReference && (
        <CalendarV2AppointmentPopover
          event={openPopoverEvent}
          anchorReference={openPopoverAnchorReference}
          open
          onClose={closeAppointmentPopover}
          onPointerEnter={clearPopoverTimers}
          onPointerLeave={handlePopoverSurfaceLeave}
          onInteractionPin={handlePopoverInteractionPin}
          onOpenAppointmentDetail={onAppointmentOpen}
        />
      )}

      {/* Add Appointment Modal - hidden in read-only mode */}
      {!isReadOnly && (
        <AddAppointmentModal
          open={isAddAppointmentModalOpen}
          onClose={handleCloseAddAppointmentModal}
          initialMode={selectedBlockedTime || selectedAllDay ? 'blocked_time' : 'appointment'}
          initialScheduledTime={selectedDateTime}
          initialDuration={selectedDuration}
          initialProviderId={selectedProviderId}
          initialAllDay={selectedAllDay}
          blockedTime={selectedBlockedTime}
          existingAppointments={appointments}
          onAppointmentCreated={handleAppointmentCreated}
          onBlockedTimeSaved={handleBlockedTimeChanged}
          onBlockedTimeDeleted={handleBlockedTimeChanged}
        />
      )}
    </CalendarContainer>
  );
};

export default CalendarWidget;
